import React, { FC, useCallback, useRef } from 'react';
import styled from 'styled-components';
import { Colors } from '@inplayer-org/inplayer-ui';
import noop from 'lodash/noop';

interface StyledDivProps {
  isDisabled: boolean;
}

const StyledDiv = styled.div<StyledDivProps>`
  ${({ isDisabled, theme }) => `
    position: relative;
    border: 1px solid ${Colors.gray};
    border-radius: 0.188em;
    color: ${theme.palette.text.light};
    cursor: pointer;
    font-size: ${theme.font.sizes.small};
    font-weight: ${theme.font.weights.light};
    outline: none;
    transition: all 0.3s ease;
    padding: 0.6em 1.8em 0.5em 0.8em;
    line-height: 1.5em;
    background-color: ${Colors.white};
    user-select: none;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    vertical-align: middle;

    ::before {
      content: '\\200b';
    }

    ::after {
      content: '';
      position: absolute;
      top: 50%;
      transform: translateY(-75%) rotate(45deg);
      right: 0.75em;
      border: solid ${Colors.gray};
      border-width: 0 2px 2px 0;
      display: inline-block;
      padding: 3px;
    }

    ${
      isDisabled
        ? `
            color: ${Colors.fontLightGray};
            border-color: ${Colors.fontLightGray};
            cursor: default;
          `
        : `
            &:hover {
              color: ${theme.palette.primary.main};
              border-color: ${theme.palette.primary.main};
            }
          `
    }
  `}
`;

const ErrorMessage = styled.div`
  margin: 0.5em 0;
  color: ${Colors.red};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  ::before {
    content: '\\200b';
  }
`;

interface Props {
  children: string;
  onClick: (rect: DOMRect) => void;
  isDisabled?: boolean;
  error?: any;
  dontDisplayError?: boolean;
}

const Select: FC<Props> = ({ onClick, isDisabled = false, children: label, error, dontDisplayError = false }) => {
  const selfRef = useRef<HTMLDivElement>(null);

  const clickHandler = useCallback(() => {
    const { current: selfElement } = selfRef;

    if (selfElement) {
      const rect = selfElement.getBoundingClientRect();
      onClick(rect);
    }
  }, [onClick]);

  const errorMessage = error ? '*Required' : '';

  return (
    <div>
      <StyledDiv ref={selfRef} onClick={isDisabled ? noop : clickHandler} isDisabled={isDisabled}>
        {label}
      </StyledDiv>
      {!dontDisplayError && <ErrorMessage title={errorMessage}>{errorMessage}</ErrorMessage>}
    </div>
  );
};

export default Select;
